export const debounce = (func, waitFor) => {
  let timeout = null;

  const debounced = (...args) => {
    if (timeout !== null) {
      clearTimeout(timeout);
      timeout = null;
    }
    timeout = setTimeout(() => func(...args), waitFor);
  };

  return debounced;
};

export const toHoursAndMinutes = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return { hours, minutes };
};

export const getFormattedTimeInHoursAndMinuteByMinutes = (totalMinutes) => {
  let label =
    (toHoursAndMinutes(totalMinutes)?.hours
      ? `${toHoursAndMinutes(totalMinutes)?.hours} hour${
          toHoursAndMinutes(totalMinutes)?.hours > 1 ? "s" : ""
        } `
      : "") +
    (toHoursAndMinutes(totalMinutes)?.minutes
      ? `${toHoursAndMinutes(totalMinutes)?.minutes} minute${
          toHoursAndMinutes(totalMinutes)?.minutes > 1 ? "s" : ""
        }`
      : "");
  return label || "0 hours";
};

export const calculateTimeDifference = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return "Invalid dates";
  }

  try {
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Check if dates are valid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return "Invalid dates";
    }

    const diffInMs = end.getTime() - start.getTime();
    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

    if (diffInMinutes < 0) {
      return "End date is before start date";
    }

    const hours = Math.floor(diffInMinutes / 60);
    const minutes = diffInMinutes % 60;
    const seconds = diffInSeconds % 60;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;

    let result = "";

    if (days > 0) {
      result += `${days} day${days > 1 ? "s" : ""} `;
    }

    if (remainingHours > 0) {
      result += `${remainingHours}h:${minutes
        .toString()
        .padStart(2, "0")}m:${seconds.toString().padStart(2, "0")}s`;
    } else if (minutes > 0) {
      result += `${minutes}m:${seconds.toString().padStart(2, "0")}s`;
    } else if (seconds > 0) {
      result += `${seconds}s`;
    }

    return result.trim() || "0 m";
  } catch (error) {
    console.error("Error calculating time difference:", error);
    return "Error calculating time";
  }
};

export const formatDateForCard = (dateString) => {
  if (!dateString) return "";

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return "";
    }

    // Format as "Nov 6, 2025" style
    const options = {
      month: "short",
      day: "numeric",
      year: "numeric",
    };

    return date.toLocaleDateString("en-US", options);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
};

// Utility function to determine time trial task status
export const getTimeTrialStatus = (task) => {
  // If task has been submitted, return the submission status in uppercase
  if (task?.taskCompletion?.status) {
    return task.taskCompletion.status.toUpperCase();
  }

  // If no submission, check if task has expired
  if (task?.endDate) {
    const now = new Date();
    const endDateTime = new Date(task.endDate);

    // Compare full datetime including time
    if (now > endDateTime) {
      return "EXPIRED";
    }
  }

  // Default status for active tasks without submission
  return "PENDING";
};

// Utility function to format status with proper capitalization
export const formatStatus = (status) => {
  if (!status) return "";

  const statusMap = {
    PENDING: "Pending",
    EXPIRED: "Expired",
    APPROVED: "Approved",
    REJECTED: "Rejected",
    RESEND: "Resend",
    ACTIVE: "Active",
    SCHEDULED: "Scheduled",
    INACTIVE: "Inactive",
    INCOMPLETE: "Incomplete",
  };

  return statusMap[status] || status;
};
